import clsx from 'clsx'
import React, { ElementType, PropsWithChildren } from 'react'
import { SubmitError, SubmitErrorType } from '@typesApp/SubmitError'
import { useAppTranslation } from '@hooks/useAppTranslation'
import { Alert, AlertContent, AlertText } from '@components/ui-css/Alert/Alert'
import { Modal, ModalActionContainer, ModalContent, ModalFooter, ModalHeader } from '@components/ui-css/Modal/Modal'
import { SVGIcon } from '@components/ui-css/SVGIcon/SVGIcon'
import { Button } from '@components/ui-css/Button/Button'
import { injectDataAttributes } from '@utils/dataAttributes'
import styles from '../styles/SubmitErrorListener.module.scss'

type ContentItem = {
  value: string
  items?: string[]
}

type LocalModalProps = {
  onClose: () => void
}

type ClaimErrorModalProps = LocalModalProps & {
  error: SubmitError
}

export const ClaimErrorModal: React.FC<ClaimErrorModalProps> = props => {
  const { error, onClose } = props

  const modals = {
    LIMIT_REACHED: LimitReachedModal,
    CLAIM_ACKNOWLEDGED: AcknowledgedModal,
    SUBMIT_CLAIM_NO_RETRY_ERROR: NoRetryErrorModal,
  } as Record<SubmitErrorType, ElementType<LocalModalProps>>

  const DynamicErrorModal = modals[error.type]
  return DynamicErrorModal ? <DynamicErrorModal onClose={onClose} /> : null
}

type FooterButtonProps = PropsWithChildren & {
  onClose: () => void
  dataAttributesKey: string
}

const FooterButton: React.FC<FooterButtonProps> = props => {
  const { onClose, dataAttributesKey, children } = props

  return (
    <ModalActionContainer>
      <Button onClick={onClose} {...injectDataAttributes(dataAttributesKey)}>
        {children}
        <SVGIcon library="base" name="arrow-right" />
      </Button>
    </ModalActionContainer>
  )
}

const AcknowledgedModal: React.FC<LocalModalProps> = props => {
  const { onClose } = props
  const { t } = useAppTranslation({ keyPrefix: 'SubmitError.acknowledged' })

  return (
    <Modal open={true} onClose={onClose}>
      <ModalHeader>{t('title')}</ModalHeader>
      <ModalContent>
        <Alert severity={'warning'}>
          <AlertContent>
            <AlertText text={t('message')} />
          </AlertContent>
        </Alert>
      </ModalContent>
      <ModalFooter>
        <FooterButton onClose={onClose} dataAttributesKey="modal.proceedToCheckout">
          {t('proceed-cta')}
        </FooterButton>
      </ModalFooter>
    </Modal>
  )
}

const LimitReachedModal: React.FC<LocalModalProps> = props => {
  const { onClose } = props
  const { t } = useAppTranslation({ keyPrefix: 'SubmitError.limitReached' })

  return (
    <Modal open={true} onClose={onClose}>
      <ModalHeader>{t('title')}</ModalHeader>
      <ModalContent>
        <Alert severity={'warning'}>
          <AlertContent>
            <AlertText text={t('message')} />
          </AlertContent>
        </Alert>
      </ModalContent>
      <ModalFooter>
        <FooterButton onClose={onClose} dataAttributesKey="modal.close">
          {t('close-cta')}
        </FooterButton>
      </ModalFooter>
    </Modal>
  )
}

const TimeoutRetryModal: React.FC<LocalModalProps> = props => {
  const { onClose } = props
  const { t } = useAppTranslation({ keyPrefix: 'SubmitError.timeoutRetry' })

  const items = t('content', { returnObjects: true }) as ContentItem[]

  return (
    <Modal open={true} onClose={onClose}>
      <ModalHeader>{t('title')}</ModalHeader>
      <ModalContent>
        <ol className={styles.orderedList}>
          {items.map((item, index) => {
            return (
              <li key={`level1-${index}`}>
                <span>{item.value}</span>
                {item?.items && (
                  <ol className={clsx(styles.orderedList, styles.alphaOrderedList)}>
                    {item?.items.map((value, index) => (
                      <li key={`level2-${index}`}>
                        <span>{value}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            )
          })}
        </ol>
      </ModalContent>
      <ModalFooter>
        <FooterButton onClose={onClose} dataAttributesKey="modal.close">
          {t('close-cta')}
        </FooterButton>
      </ModalFooter>
    </Modal>
  )
}

const NoRetryErrorModal: React.FC<LocalModalProps> = props => {
  const { onClose } = props
  const { t } = useAppTranslation({ keyPrefix: 'SubmitError.noRetryError' })

  return (
    <Modal open={true} onClose={onClose}>
      <ModalHeader>{t('title')}</ModalHeader>
      <ModalContent>
        <Alert severity={'warning'}>
          <AlertContent>
            <AlertText text={t('message')} />
          </AlertContent>
        </Alert>
      </ModalContent>
      <ModalFooter>
        <FooterButton onClose={onClose} dataAttributesKey="modal.proceedToCheckout">
          {t('proceed-cta')}
        </FooterButton>
      </ModalFooter>
    </Modal>
  )
}
